/**
 * Patients and family contracts (BUILD-PLAN 2.4 "People", M04, 5.3).
 *
 * Wave-1 interface: `Patient`, `Household`, `ContactPoint`, plus search,
 * demographics update with field-level merge, and SMS consent rules.
 *
 * Tenancy (ADR-0012): patients, households and contact points are shared by
 * a practice's offices, so they carry `practiceId` and a `homeLocationId`
 * default, not a `locationId` boundary. Office-level access is still enforced
 * by RLS (a user with no office access sees no patients).
 *
 * Open Dental numbering (ADR-0004). Source: Open Dental database
 * documentation, table `patient` (PatStatus, Gender, Position, Guarantor),
 * schema version 24.3 (opendental.com, "Database Documentation").
 */
import { z } from 'zod';
import {
  IdempotencyKeySchema,
  IdSchema,
  InstantSchema,
  LocalDateSchema,
  PmsAdapterSchema,
  PmsSystemSchema,
  PracticeOwnedSchema,
  refineSyncEnvelope,
  RowMetaSchema,
  RowVersionSchema,
  SyncEnvelopeShape,
  WriteBackStateSchema,
} from './sync.js';

/* ------------------------------------------------------------------------ */
/* Open Dental enums                                                         */
/* ------------------------------------------------------------------------ */

/** OD `PatientStatus` (patient.PatStatus). */
export const OD_PATIENT_STATUS = {
  Patient: 0,
  NonPatient: 1,
  Inactive: 2,
  Archived: 3,
  Deleted: 4,
  Deceased: 5,
  Prospective: 6,
} as const;

/** OD `PatientGender` (patient.Gender): 0 Male, 1 Female, 2 Unknown, 3 Other. */
export const OD_PATIENT_GENDER = { Male: 0, Female: 1, Unknown: 2, Other: 3 } as const;
export const PatientGenderSchema = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
  z.literal(3),
]);
export type PatientGender = z.infer<typeof PatientGenderSchema>;

/** OD `PatientPosition` (patient.Position, marital status): 0..4. */
export const OD_PATIENT_POSITION = {
  Single: 0,
  Married: 1,
  Child: 2,
  Widowed: 3,
  Divorced: 4,
} as const;
export const PatientPositionSchema = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
]);
export type PatientPosition = z.infer<typeof PatientPositionSchema>;

/* ------------------------------------------------------------------------ */
/* Status                                                                    */
/* ------------------------------------------------------------------------ */

/** Velora canonical patient status (BUILD-PLAN 2.4). */
export const PATIENT_STATUSES = [
  'active',
  'inactive',
  'archived',
  'deceased',
  'non_patient',
  'prospective',
] as const;
export const PatientStatusSchema = z.enum(PATIENT_STATUSES);
export type PatientStatus = z.infer<typeof PatientStatusSchema>;

/**
 * Map OD PatStatus to Velora. OD `Deleted` (4) is not a status in Velora: the
 * row is soft-deleted (`deletedAt` set), so this returns `'deleted'` for the
 * adapter to handle.
 */
export function patientStatusFromOd(patStatus: number): PatientStatus | 'deleted' {
  switch (patStatus) {
    case OD_PATIENT_STATUS.Patient:
      return 'active';
    case OD_PATIENT_STATUS.NonPatient:
      return 'non_patient';
    case OD_PATIENT_STATUS.Inactive:
      return 'inactive';
    case OD_PATIENT_STATUS.Archived:
      return 'archived';
    case OD_PATIENT_STATUS.Deleted:
      return 'deleted';
    case OD_PATIENT_STATUS.Deceased:
      return 'deceased';
    case OD_PATIENT_STATUS.Prospective:
      return 'prospective';
    default:
      throw new RangeError(`unknown Open Dental PatStatus ${patStatus}`);
  }
}

/** Inverse of `patientStatusFromOd`. */
export function patientStatusToOd(status: PatientStatus): number {
  const map: Record<PatientStatus, number> = {
    active: OD_PATIENT_STATUS.Patient,
    non_patient: OD_PATIENT_STATUS.NonPatient,
    inactive: OD_PATIENT_STATUS.Inactive,
    archived: OD_PATIENT_STATUS.Archived,
    deceased: OD_PATIENT_STATUS.Deceased,
    prospective: OD_PATIENT_STATUS.Prospective,
  };
  return map[status];
}

/** Statuses that may receive outreach (recall, reminders, marketing). Deceased never do (M04). */
export const OUTREACH_PATIENT_STATUSES: ReadonlySet<PatientStatus> = new Set<PatientStatus>([
  'active',
  'prospective',
]);

/* ------------------------------------------------------------------------ */
/* Patient                                                                   */
/* ------------------------------------------------------------------------ */

/** US postal address. `state` is the USPS two-letter code. */
export const PostalAddressSchema = z.object({
  line1: z.string().max(100).nullable(),
  line2: z.string().max(100).nullable(),
  city: z.string().max(100).nullable(),
  state: z
    .string()
    .regex(/^[A-Z]{2}$/, 'state is a two-letter USPS code')
    .nullable(),
  postalCode: z
    .string()
    .regex(/^\d{5}(?:-\d{4})?$/, 'postal code is ZIP or ZIP+4')
    .nullable(),
});
export type PostalAddress = z.infer<typeof PostalAddressSchema>;

const NameSchema = z.string().trim().min(1).max(100);

const PatientObject = z.object({
  id: IdSchema,
  ...PracticeOwnedSchema.shape,
  /** Default office for this patient; not an access boundary. */
  homeLocationId: IdSchema.nullable(),
  householdId: IdSchema.nullable(),
  firstName: NameSchema,
  lastName: NameSchema,
  middleInitial: z.string().max(5).nullable(),
  preferredName: z.string().max(100).nullable(),
  birthdate: LocalDateSchema.nullable(),
  gender: PatientGenderSchema.nullable(),
  maritalPosition: PatientPositionSchema.nullable(),
  status: PatientStatusSchema,
  /** BCP 47 tag (`en`, `es`, `es-MX`); drives template language. */
  language: z
    .string()
    .regex(/^[a-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/, 'language is a BCP 47 tag')
    .nullable(),
  chartNumber: z.string().max(20).nullable(),
  primaryProviderId: IdSchema.nullable(),
  secondaryProviderId: IdSchema.nullable(),
  referralSourceId: IdSchema.nullable(),
  employerId: IdSchema.nullable(),
  firstVisitDate: LocalDateSchema.nullable(),
  address: PostalAddressSchema,
  /**
   * Last four SSN digits for display only. The full SSN is never part of any
   * contract; reading it needs `patient.view_ssn` and a dedicated endpoint.
   */
  ssnLast4: z
    .string()
    .regex(/^\d{4}$/)
    .nullable(),
  flags: z.array(z.string().min(1).max(40)),
  ...RowMetaSchema.shape,
  ...SyncEnvelopeShape,
  writeBack: WriteBackStateSchema,
});

function refinePatient(p: z.infer<typeof PatientObject>, ctx: z.RefinementCtx): void {
  refineSyncEnvelope(p, ctx);
  if (p.birthdate !== null && p.firstVisitDate !== null && p.firstVisitDate < p.birthdate) {
    ctx.addIssue({
      code: 'custom',
      path: ['firstVisitDate'],
      message: 'first visit is before birth',
    });
  }
}

/** A stored patient as the patients API returns it. */
export const PatientSchema = PatientObject.superRefine(refinePatient);
export type Patient = z.infer<typeof PatientSchema>;

/** Display name rule: preferred name when set, then last name. */
export function patientDisplayName(
  p: Pick<Patient, 'firstName' | 'lastName' | 'preferredName'>,
): string {
  const first =
    p.preferredName !== null && p.preferredName.trim() !== '' ? p.preferredName : p.firstName;
  return `${first} ${p.lastName}`;
}

/** Search hit / palette row / peek header. */
export const PatientSummarySchema = z.object({
  id: IdSchema,
  practiceId: IdSchema,
  homeLocationId: IdSchema.nullable(),
  firstName: NameSchema,
  lastName: NameSchema,
  preferredName: z.string().max(100).nullable(),
  birthdate: LocalDateSchema.nullable(),
  chartNumber: z.string().max(20).nullable(),
  status: PatientStatusSchema,
  /** Last four digits of the primary phone, for disambiguation. */
  primaryPhoneLast4: z
    .string()
    .regex(/^\d{4}$/)
    .nullable(),
  householdId: IdSchema.nullable(),
});
export type PatientSummary = z.infer<typeof PatientSummarySchema>;

/* ------------------------------------------------------------------------ */
/* Search (blind index; BUILD-PLAN 2.4, M04)                                 */
/* ------------------------------------------------------------------------ */

export const PatientSearchModeSchema = z.enum(['auto', 'name', 'chart', 'phone', 'dob']);
export type PatientSearchMode = z.infer<typeof PatientSearchModeSchema>;

/**
 * `GET /v1/patients/search`. Search runs over blind indexes (HMAC of
 * normalised tokens), so only exact-token and prefix-token matches exist; the
 * query never reaches logs or URLs in clear (it is sent as a POST body).
 */
export const PatientSearchQuerySchema = z
  .object({
    q: z.string().trim().min(2).max(100),
    mode: PatientSearchModeSchema.default('auto'),
    /** Inactive, archived and non-patients are hidden unless toggled (M04). */
    includeInactive: z.boolean().default(false),
    /** Restrict to patients whose home office is in the current office scope. */
    homeLocationOnly: z.boolean().default(false),
    limit: z.number().int().min(1).max(100).default(25),
  })
  .strict()
  .superRefine((s, ctx) => {
    if (s.mode === 'phone' && s.q.replace(/\D/g, '').length < 4) {
      ctx.addIssue({
        code: 'custom',
        path: ['q'],
        message: 'phone search needs at least 4 digits',
      });
    }
  });
export type PatientSearchQueryInput = z.input<typeof PatientSearchQuerySchema>;
export type PatientSearchQuery = z.output<typeof PatientSearchQuerySchema>;

/**
 * Resolve `mode: 'auto'`: 4+ digits only is a phone (last-four search works),
 * a date is a birthdate, `#123` or `chart:123` is a chart number, anything
 * else is a name.
 */
export function resolveSearchMode(q: string): Exclude<PatientSearchMode, 'auto'> {
  const t = q.trim();
  if (/^(?:#|chart:)\s*\S+$/i.test(t)) return 'chart';
  // ISO dates and US month/day/year with slashes or dashes are birthdates
  // (checked before phone, because 12-25-1980 is also eight digits).
  if (/^\d{4}-\d{2}-\d{2}$/.test(t) || /^\d{1,2}([/-])\d{1,2}\1\d{4}$/.test(t)) return 'dob';
  const digits = t.replace(/[\s().+-]/g, '');
  if (/^\d{4,15}$/.test(digits)) return 'phone';
  return 'name';
}

/* ------------------------------------------------------------------------ */
/* Household (OD-style guarantor family)                                     */
/* ------------------------------------------------------------------------ */

export const HOUSEHOLD_RELATIONSHIPS = [
  'self',
  'spouse',
  'child',
  'parent',
  'sibling',
  'partner',
  'other',
] as const;
export const HouseholdRelationshipSchema = z.enum(HOUSEHOLD_RELATIONSHIPS);
export type HouseholdRelationship = z.infer<typeof HouseholdRelationshipSchema>;

export const HouseholdMemberSchema = z.object({
  patientId: IdSchema,
  /** Relationship to the guarantor. */
  relationship: HouseholdRelationshipSchema,
  isGuarantor: z.boolean(),
});
export type HouseholdMember = z.infer<typeof HouseholdMemberSchema>;

/**
 * A guarantor family (OD: every patient's `Guarantor` points at the head of
 * family). Exactly one member is the guarantor; the guarantor's relationship
 * is `self`.
 */
export const HouseholdSchema = z
  .object({
    id: IdSchema,
    ...PracticeOwnedSchema.shape,
    name: z.string().max(200),
    guarantorPatientId: IdSchema.nullable(),
    members: z.array(HouseholdMemberSchema),
    ...RowMetaSchema.shape,
    ...SyncEnvelopeShape,
  })
  .superRefine((h, ctx) => {
    refineSyncEnvelope(h, ctx);
    const seen = new Set<string>();
    h.members.forEach((m, i) => {
      if (seen.has(m.patientId)) {
        ctx.addIssue({
          code: 'custom',
          path: ['members', i, 'patientId'],
          message: 'duplicate member',
        });
      }
      seen.add(m.patientId);
      if (m.isGuarantor && m.relationship !== 'self') {
        ctx.addIssue({
          code: 'custom',
          path: ['members', i, 'relationship'],
          message: 'the guarantor relationship is "self"',
        });
      }
    });
    const guarantors = h.members.filter((m) => m.isGuarantor);
    if (guarantors.length > 1) {
      ctx.addIssue({ code: 'custom', path: ['members'], message: 'a household has one guarantor' });
    }
    if (h.members.length > 0 && guarantors.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['members'],
        message: 'a household with members needs a guarantor',
      });
    }
    if (h.guarantorPatientId !== (guarantors[0]?.patientId ?? null)) {
      ctx.addIssue({
        code: 'custom',
        path: ['guarantorPatientId'],
        message: 'guarantorPatientId must be the member flagged isGuarantor',
      });
    }
  });
export type Household = z.infer<typeof HouseholdSchema>;

/**
 * Effective guarantor PMS id for Dentrix rows: `COALESCE(NULLIF(guarId, 0),
 * patId)` (M04 acceptance). A zero, empty or missing guarantor means the
 * patient is their own guarantor.
 */
export function effectiveGuarantorPmsId(
  guarantorPmsId: string | number | null | undefined,
  patientPmsId: string | number,
): string {
  const g =
    guarantorPmsId === null || guarantorPmsId === undefined ? '' : String(guarantorPmsId).trim();
  return g === '' || g === '0' ? String(patientPmsId) : g;
}

/* ------------------------------------------------------------------------ */
/* Contact points and consent                                                */
/* ------------------------------------------------------------------------ */

/** E.164: `+`, country code, up to 15 digits total (ITU-T E.164). */
export const E164Schema = z.string().regex(/^\+[1-9]\d{6,14}$/, 'phone must be E.164');

export const ContactKindSchema = z.enum(['phone', 'email']);
export const PhoneTypeSchema = z.enum(['mobile', 'home', 'work', 'other']);
export type PhoneType = z.infer<typeof PhoneTypeSchema>;

/**
 * Consent categories for SMS (texting-inbox 4.7). Billing and review are kept
 * apart from transactional because the TCPA healthcare exemption covers
 * appointment and care messages only and expressly excludes telemarketing,
 * solicitation, advertising, billing, debt collection and other financial
 * content (FCC 15-72, 2015 TCPA Omnibus Declaratory Ruling and Order,
 * 30 FCC Rcd 7961, paragraphs 146-147; 47 U.S.C. 227(b)(1)(A)(iii)).
 * Confirm current scope with counsel before launch.
 */
export const SMS_PURPOSES = ['transactional', 'billing', 'marketing', 'review'] as const;
export const SmsPurposeSchema = z.enum(SMS_PURPOSES);
export type SmsPurpose = z.infer<typeof SmsPurposeSchema>;

export const ContactPointSchema = z
  .object({
    id: IdSchema,
    ...PracticeOwnedSchema.shape,
    patientId: IdSchema,
    kind: ContactKindSchema,
    phoneType: PhoneTypeSchema.nullable(),
    /** As entered or as received from the PMS. */
    value: z.string().min(1).max(320),
    /** E.164 for phones, lower-cased for email; the blind-index input. */
    valueNormalized: z.string().min(1).max(320),
    isPrimary: z.boolean(),
    /** Appointment reminders, confirmations, care instructions. */
    transactionalSms: z.boolean(),
    /** Balance and payment messages (not covered by the healthcare exemption). */
    billingSms: z.boolean(),
    marketingSms: z.boolean(),
    reviewSms: z.boolean(),
    emailOk: z.boolean(),
    /** Set by STOP (or a staff-confirmed revocation); clears every SMS category. */
    smsOptedOutAt: InstantSchema.nullable(),
    emailOptedOutAt: InstantSchema.nullable(),
    ...RowMetaSchema.shape,
    ...SyncEnvelopeShape,
  })
  .superRefine((c, ctx) => {
    refineSyncEnvelope(c, ctx);
    if (c.kind === 'email' && c.phoneType !== null) {
      ctx.addIssue({ code: 'custom', path: ['phoneType'], message: 'emails have no phoneType' });
    }
    if (c.kind === 'phone' && !E164Schema.safeParse(c.valueNormalized).success) {
      ctx.addIssue({
        code: 'custom',
        path: ['valueNormalized'],
        message: 'phone must normalise to E.164',
      });
    }
    if (c.kind === 'email' && !z.email().safeParse(c.valueNormalized).success) {
      ctx.addIssue({ code: 'custom', path: ['valueNormalized'], message: 'email is not valid' });
    }
    // The blind index is an HMAC of the normalised value; a mixed-case email
    // would index differently from the same address typed in lower case.
    if (c.kind === 'email' && c.valueNormalized !== c.valueNormalized.toLowerCase()) {
      ctx.addIssue({
        code: 'custom',
        path: ['valueNormalized'],
        message: 'normalised email is lower case',
      });
    }
    if (c.emailOptedOutAt !== null && c.emailOk) {
      ctx.addIssue({
        code: 'custom',
        path: ['emailOptedOutAt'],
        message: 'an opted-out email has email consent off',
      });
    }
    if (
      c.kind === 'email' &&
      (c.transactionalSms || c.billingSms || c.marketingSms || c.reviewSms)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['transactionalSms'],
        message: 'SMS consent applies to phones only',
      });
    }
    if (c.kind === 'phone' && c.emailOk) {
      ctx.addIssue({
        code: 'custom',
        path: ['emailOk'],
        message: 'email consent applies to emails only',
      });
    }
    if (
      c.smsOptedOutAt !== null &&
      (c.transactionalSms || c.billingSms || c.marketingSms || c.reviewSms)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['smsOptedOutAt'],
        message: 'an opted-out number has every SMS category off',
      });
    }
  });
export type ContactPoint = z.infer<typeof ContactPointSchema>;

export type SmsDecision =
  | { send: true }
  | { send: false; reason: 'not_a_phone' | 'opted_out' | 'no_consent' | 'patient_status' }
  | { send: 'warn'; reason: 'opted_out_patient_initiated' };

/**
 * Whether an SMS may go to this contact point.
 *
 * - Automated sends need the category's consent and no opt-out.
 * - After an opt-out, the only automated message is the single opt-out
 *   confirmation the consent engine sends itself (FCC 24-24, 2024 TCPA
 *   revocation order; 47 CFR 64.1200(a)(10)-(12); CTIA Messaging Principles
 *   and Best Practices, May 2023).
 * - A staff member may reply by hand to a patient who texted the office after
 *   opting out (a patient-initiated, one-to-one exchange), with a warning
 *   (M11 "staff manual sends warn"). Otherwise manual sends are blocked too.
 * - Deceased and archived patients never receive outreach (M04); manual
 *   transactional replies are still allowed for non-outreach statuses.
 *   Automated sends without a `patientStatus` are refused.
 * - `lastInboundAt` must be the last inbound message that was not itself a
 *   consent keyword (the STOP that caused the opt-out does not count as the
 *   patient writing in again), stamped on the same clock as `smsOptedOutAt`.
 */
export function smsDecision(
  cp: Pick<
    ContactPoint,
    'kind' | 'smsOptedOutAt' | 'transactionalSms' | 'billingSms' | 'marketingSms' | 'reviewSms'
  >,
  purpose: SmsPurpose,
  opts: {
    mode: 'automated' | 'manual';
    patientStatus?: PatientStatus;
    /** Last inbound message from this number on this line. */
    lastInboundAt?: string | null;
  },
): SmsDecision {
  if (cp.kind !== 'phone') return { send: false, reason: 'not_a_phone' };
  if (opts.patientStatus === 'deceased') return { send: false, reason: 'patient_status' };
  // Fail closed: automation must know the patient status, or a deceased or
  // inactive patient could be texted because a caller forgot to pass it (M04
  // "deceased never appear in outreach lists"; M12 acceptance).
  if (
    opts.mode === 'automated' &&
    (opts.patientStatus === undefined || !OUTREACH_PATIENT_STATUSES.has(opts.patientStatus))
  ) {
    return { send: false, reason: 'patient_status' };
  }
  if (cp.smsOptedOutAt !== null) {
    const inbound = opts.lastInboundAt ?? null;
    if (
      opts.mode === 'manual' &&
      inbound !== null &&
      Date.parse(inbound) > Date.parse(cp.smsOptedOutAt)
    ) {
      return { send: 'warn', reason: 'opted_out_patient_initiated' };
    }
    return { send: false, reason: 'opted_out' };
  }
  const consent: Record<SmsPurpose, boolean> = {
    transactional: cp.transactionalSms,
    billing: cp.billingSms,
    marketing: cp.marketingSms,
    review: cp.reviewSms,
  };
  if (!consent[purpose]) {
    // A manual transactional reply in an active conversation the patient
    // started is a direct response, not an outbound campaign.
    if (
      opts.mode === 'manual' &&
      purpose === 'transactional' &&
      (opts.lastInboundAt ?? null) !== null
    ) {
      return { send: true };
    }
    return { send: false, reason: 'no_consent' };
  }
  return { send: true };
}

/**
 * Whether a consent change may be applied to a contact point.
 *
 * Once a number has opted out (STOP), SMS consent may only be restored by the
 * consumer: a later START-family keyword from that number or the patient's
 * own preference link (texting-inbox 4.7 "Staff cannot re-opt-in by toggle";
 * OD: "No one at the office or at Open Dental can opt a patient back in").
 * Staff toggles, intake forms, imports and PMS sync (for example OD
 * `TxtMsgOk` flipping back to Yes) never override a STOP. Revocations are
 * always allowed (47 CFR 64.1200(a)(10): revocation by any reasonable means).
 */
export function isSmsConsentGrantAllowed(
  cp: Pick<ContactPoint, 'smsOptedOutAt'>,
  change: {
    category: 'all_sms' | SmsPurpose | 'email';
    granted: boolean;
    source: 'keyword' | 'patient_link' | 'staff' | 'intake_form' | 'import' | 'pms_sync';
    occurredAt: string;
  },
): boolean {
  if (!change.granted || change.category === 'email' || cp.smsOptedOutAt === null) return true;
  if (change.source !== 'keyword' && change.source !== 'patient_link') return false;
  return Date.parse(change.occurredAt) > Date.parse(cp.smsOptedOutAt);
}

/* ------------------------------------------------------------------------ */
/* Alerts (readiness chips, medical banner)                                  */
/* ------------------------------------------------------------------------ */

export const PatientAlertSchema = z.object({
  id: IdSchema,
  ...PracticeOwnedSchema.shape,
  patientId: IdSchema,
  kind: z.enum(['medical', 'allergy', 'premed', 'medication', 'other']),
  description: z.string().min(1).max(500),
  /** SNOMED CT concept id where known (digits, 6..18). */
  snomedCode: z
    .string()
    .regex(/^\d{6,18}$/)
    .nullable(),
  severity: z.enum(['info', 'warning', 'critical']).nullable(),
  active: z.boolean(),
});
export type PatientAlert = z.infer<typeof PatientAlertSchema>;

/* ------------------------------------------------------------------------ */
/* Demographics update with field-level merge (BUILD-PLAN 2.5.5)             */
/* ------------------------------------------------------------------------ */

/** Fields staff may edit through `patient.update`. */
export const PatientEditableSchema = z
  .object({
    firstName: NameSchema,
    lastName: NameSchema,
    middleInitial: z.string().max(5).nullable(),
    preferredName: z.string().max(100).nullable(),
    birthdate: LocalDateSchema.nullable(),
    gender: PatientGenderSchema.nullable(),
    maritalPosition: PatientPositionSchema.nullable(),
    status: PatientStatusSchema,
    language: PatientObject.shape.language,
    chartNumber: z.string().max(20).nullable(),
    primaryProviderId: IdSchema.nullable(),
    secondaryProviderId: IdSchema.nullable(),
    address: PostalAddressSchema,
  })
  .partial()
  .strict();
export type PatientEditable = z.infer<typeof PatientEditableSchema>;

/**
 * `PATCH /v1/patients/:id`. `base` carries the value of every changed field
 * as the editor last read it, so the server can three-way merge against a
 * concurrent PMS edit: disjoint fields merge, the same field opens a conflict
 * (M04 acceptance).
 */
export const PatientUpdateRequestSchema = z
  .object({
    expectedVersion: RowVersionSchema,
    idempotencyKey: IdempotencyKeySchema,
    changes: PatientEditableSchema,
    base: z.record(z.string(), z.unknown()),
  })
  .strict()
  .superRefine((r, ctx) => {
    const keys = Object.keys(r.changes);
    if (keys.length === 0) {
      ctx.addIssue({ code: 'custom', path: ['changes'], message: 'no changes' });
    }
    for (const k of keys) {
      if (!(k in r.base)) {
        ctx.addIssue({
          code: 'custom',
          path: ['base', k],
          message: `base value for "${k}" is required`,
        });
      }
    }
  });
export type PatientUpdateRequest = z.infer<typeof PatientUpdateRequestSchema>;

/** `POST /v1/patients/:id/contact-points` and its PATCH. */
export const ContactPointInputSchema = z
  .object({
    kind: ContactKindSchema,
    phoneType: PhoneTypeSchema.nullable().default(null),
    value: z.string().trim().min(3).max(320),
    isPrimary: z.boolean().default(false),
    idempotencyKey: IdempotencyKeySchema,
  })
  .strict();
export type ContactPointInputValue = z.input<typeof ContactPointInputSchema>;

/* ======================================================================== */
/* Patients module API (WP1-PATIENTS, routes under /v1/patients)            */
/*                                                                          */
/* Everything below is additive to the frozen wave-1 shapes above.          */
/* ======================================================================== */

/** SSE entity key for patient rows; household changes publish under it too. */
export const PATIENT_ENTITY_KEY = 'patient';

/** Fields the editor shows as one value each (the address is one field). */
export const PATIENT_EDIT_FIELDS = [
  'firstName',
  'lastName',
  'middleInitial',
  'preferredName',
  'birthdate',
  'gender',
  'maritalPosition',
  'status',
  'language',
  'chartNumber',
  'primaryProviderId',
  'secondaryProviderId',
  'address',
] as const satisfies readonly (keyof PatientEditable)[];
export type PatientEditField = (typeof PATIENT_EDIT_FIELDS)[number];

/** Whole years between a birthdate and a local date (both `YYYY-MM-DD`). */
export function ageOn(birthdate: string, today: string): number {
  const [by, bm, bd] = birthdate.split('-').map(Number) as [number, number, number];
  const [ty, tm, td] = today.split('-').map(Number) as [number, number, number];
  let age = ty - by;
  if (tm < bm || (tm === bm && td < bd)) age -= 1;
  return age;
}

/** Minors cannot be guarantor (patient-file PF-03); an 18th birthday re-enables it. */
export function isMinorOn(birthdate: string | null, today: string): boolean {
  return birthdate !== null && ageOn(birthdate, today) < 18;
}

/* ---------- field-level three-way merge (BUILD-PLAN 2.5.5, M04) ---------- */

function canonical(value: unknown): string {
  if (value === undefined || value === null) return 'null';
  if (typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  const o = value as Record<string, unknown>;
  return `{${Object.keys(o)
    .sort()
    .map((k) => `${JSON.stringify(k)}:${canonical(o[k])}`)
    .join(',')}}`;
}

/** Deep value equality for merge decisions (`undefined` equals `null`). */
export function sameValue(a: unknown, b: unknown): boolean {
  return canonical(a) === canonical(b);
}

export interface FieldConflict {
  field: string;
  /** What the editor last read. */
  base: unknown;
  /** What the editor wants. */
  ours: unknown;
  /** What the record holds now (another user or the PMS changed it). */
  theirs: unknown;
}

export interface MergePlan {
  /** Fields to write, with the editor's value. */
  apply: Record<string, unknown>;
  /** Fields where the record already holds the wanted value. */
  unchanged: string[];
  /** Fields changed on both sides to different values: never written. */
  conflicts: FieldConflict[];
}

/**
 * Field-level merge of an edit against the current record. A field the
 * record still holds at its base value is applied; a field someone else
 * already set to the same value is unchanged; a field changed on both sides
 * to different values is a conflict and is not written. Disjoint edits on
 * the two sides therefore both survive (M04 acceptance, PF-07).
 */
export function planFieldMerge(
  changes: Record<string, unknown>,
  base: Record<string, unknown>,
  current: Record<string, unknown>,
): MergePlan {
  const plan: MergePlan = { apply: {}, unchanged: [], conflicts: [] };
  for (const [field, ours] of Object.entries(changes)) {
    const theirs = current[field];
    if (sameValue(theirs, ours)) plan.unchanged.push(field);
    else if (sameValue(theirs, base[field])) plan.apply[field] = ours;
    else plan.conflicts.push({ field, base: base[field] ?? null, ours, theirs: theirs ?? null });
  }
  return plan;
}

/* ---------- search ---------- */

export const PatientSearchMatchSchema = z.enum(['name', 'phone', 'dob', 'chart', 'email']);
export type PatientSearchMatch = z.infer<typeof PatientSearchMatchSchema>;

export const PatientSearchHitSchema = PatientSummarySchema.extend({
  age: z.number().int().min(0).nullable(),
  matchedOn: PatientSearchMatchSchema,
  hasCriticalAlert: z.boolean(),
  /**
   * The PMS patient id, shown in the Chart column when the PMS sends no chart
   * number (PulseSync v1, PAT-08). Absent from older servers.
   */
  pmsId: z.string().max(64).nullable().optional(),
  /**
   * The office this hit is listed under: the office selected in the office
   * selector, else (All offices) the patient's home office, else the first
   * office they were seen at. Absent from older servers.
   */
  locationId: IdSchema.nullable().optional(),
  /**
   * Offices in the request's scope the patient belongs to (home office
   * first, then offices with an appointment), for the office badges in All
   * offices mode. Absent from older servers.
   */
  officeIds: z.array(IdSchema).optional(),
});
export type PatientSearchHit = z.infer<typeof PatientSearchHitSchema>;

/** `POST /v1/patients/search` (the query travels in the body, never the URL). */
export const PatientSearchResponseSchema = z.object({
  hits: z.array(PatientSearchHitSchema),
  mode: z.enum(['name', 'chart', 'phone', 'dob']),
  /** Patients whose blind index was rebuilt during this request. */
  reindexed: z.number().int().min(0),
  /**
   * Patients still waiting to be indexed after this request (a large sync or
   * import is finished in the background). Above 0, results may be missing
   * those patients and the page says so.
   */
  pending: z.number().int().min(0).default(0),
});
export type PatientSearchResponse = z.infer<typeof PatientSearchResponseSchema>;

/* ---------- overview ---------- */

export const PersonRefSchema = z.object({ id: IdSchema, name: z.string() });
export type PersonRef = z.infer<typeof PersonRefSchema>;

export const ALERT_SCOPES = ['patient', 'household'] as const;
export const AlertScopeSchema = z.enum(ALERT_SCOPES);
export type AlertScope = z.infer<typeof AlertScopeSchema>;

export const PatientAlertViewSchema = PatientAlertSchema.extend({
  scope: AlertScopeSchema,
  /** Office-local date after which the alert stops showing (PF-20). */
  expiresAt: LocalDateSchema.nullable(),
  /** `expiresAt` has passed: kept in history, never shown as a pill. */
  expired: z.boolean(),
  createdAt: InstantSchema,
  version: RowVersionSchema,
  /** A household-scope alert that lives on another member (PF-21). */
  inheritedFrom: z.object({ id: IdSchema, name: z.string() }).nullable(),
  /** When the current user acknowledged it in this session (PF-18). */
  acknowledgedAt: InstantSchema.nullable(),
  /**
   * Where the alert came from: `pms` when the PMS link sent it, `velora` when
   * it was entered in Velora (and is not in the PMS). Optional for older
   * servers; the UI then falls back to the alerts feed state.
   */
  origin: z.enum(['pms', 'velora']).optional(),
});
export type PatientAlertView = z.infer<typeof PatientAlertViewSchema>;

/** An alert on a PMS record that the PMS does not have (entered in Velora). */
export function isVeloraOnlyAlert(a: Pick<PatientAlertView, 'origin'>, o: FeedInput): boolean {
  if (o.patient.origin !== 'pms') return false;
  if (a.origin) return a.origin !== 'pms';
  return feedNotSynced(o, 'medicalAlerts');
}

export const ContactPointViewSchema = z.object({
  id: IdSchema,
  kind: ContactKindSchema,
  phoneType: PhoneTypeSchema.nullable(),
  value: z.string(),
  valueNormalized: z.string(),
  isPrimary: z.boolean(),
  transactionalSms: z.boolean(),
  billingSms: z.boolean(),
  marketingSms: z.boolean(),
  reviewSms: z.boolean(),
  emailOk: z.boolean(),
  smsOptedOutAt: InstantSchema.nullable(),
  emailOptedOutAt: InstantSchema.nullable(),
  version: RowVersionSchema,
  updatedAt: InstantSchema,
  writeBack: WriteBackStateSchema,
  /**
   * What the carrier says the number is (Twilio Lookup, or a 30006 refusal),
   * cached per number; `landline`: Velora never texts it ("Can't text").
   * Null or absent: not checked yet.
   */
  carrierLineType: z.enum(['mobile', 'landline', 'voip', 'unknown']).nullable().optional(),
});
export type ContactPointView = z.infer<typeof ContactPointViewSchema>;

export const HouseholdMemberViewSchema = z.object({
  patientId: IdSchema,
  firstName: z.string(),
  lastName: z.string(),
  preferredName: z.string().nullable(),
  birthdate: LocalDateSchema.nullable(),
  age: z.number().int().nullable(),
  status: PatientStatusSchema,
  relationship: HouseholdRelationshipSchema,
  isGuarantor: z.boolean(),
  homeLocationId: IdSchema.nullable(),
  nextAppointmentAt: InstantSchema.nullable(),
  nextAppointmentLocationId: IdSchema.nullable(),
  recallDueDate: LocalDateSchema.nullable(),
  /** Null when the caller lacks `money.view`. */
  balanceCents: z.number().int().nullable(),
  hasCriticalAlert: z.boolean(),
  carrierName: z.string().nullable(),
  address: PostalAddressSchema,
  /** The member's own chart number (Eaglesoft family grid ID, Dentrix Chart #). */
  chartNumber: z.string().nullable(),
  /** OD `PatientGender`; null when not recorded. */
  gender: PatientGenderSchema.nullable(),
  /** OD `PatientPosition` (marital status: Single, Married, Child...); null when not recorded. */
  position: PatientPositionSchema.nullable(),
  /** Primary provider's abbreviation ("DDS1"), as the PMS family grids show it. */
  providerAbbr: z.string().nullable(),
});
export type HouseholdMemberView = z.infer<typeof HouseholdMemberViewSchema>;

export const HouseholdViewSchema = z.object({
  id: IdSchema,
  name: z.string(),
  guarantorPatientId: IdSchema.nullable(),
  accountNote: z.string().nullable(),
  version: RowVersionSchema,
  /** Why guarantor and member changes are not possible in Velora, or null. */
  lockedReason: z.string().nullable(),
  members: z.array(HouseholdMemberViewSchema),
  familyBalanceCents: z.number().int().nullable(),
});
export type HouseholdView = z.infer<typeof HouseholdViewSchema>;

export const EligibilityStateSchema = z.enum(['verified', 'unable', 'ineligible', 'not-checked']);
export type EligibilityState = z.infer<typeof EligibilityStateSchema>;

const VisitShape = {
  appointmentId: IdSchema,
  startsAt: InstantSchema,
  locationId: IdSchema,
  lengthMin: z.number().int(),
  /** Appointment type, else the scheduled procedure codes. */
  label: z.string(),
  providerName: z.string().nullable(),
  operatoryName: z.string().nullable(),
  confirmation: z.string().nullable(),
};
export const UpcomingVisitSchema = z.object(VisitShape);
export type UpcomingVisit = z.infer<typeof UpcomingVisitSchema>;

export const PatientTilesSchema = z.object({
  /** Null when the caller lacks `money.view`. */
  balance: z
    .object({
      patientCents: z.number().int(),
      familyCents: z.number().int(),
      aging: z.object({
        b0to30: z.number().int(),
        b31to60: z.number().int(),
        b61to90: z.number().int(),
        over90: z.number().int(),
      }),
      lastPaymentOn: LocalDateSchema.nullable(),
    })
    .nullable(),
  /** Null when no coverage is on file (self pay). */
  insurance: z
    .object({
      carrierName: z.string(),
      groupName: z.string().nullable(),
      eligibility: EligibilityStateSchema,
      checkedAt: InstantSchema.nullable(),
      annualMaxCents: z.number().int().nullable(),
      secondaryCarrierName: z.string().nullable(),
    })
    .nullable(),
  nextVisit: UpcomingVisitSchema.nullable(),
  /**
   * The hygiene-group recall (prophy, child prophy or perio), computed by the
   * same code as the Recall workspace so both screens give one answer (RC-05).
   */
  recall: z
    .object({
      typeName: z.string(),
      dueDate: LocalDateSchema.nullable(),
      intervalMonths: z.number().int().nullable(),
      scheduledAt: InstantSchema.nullable(),
      /** Where the due date comes from (absent from older servers). */
      source: z.enum(['pms', 'velora', 'calculated', 'none']).optional(),
      /** The booked appointment that holds this recall's codes, if any. */
      appointmentId: IdSchema.nullable().optional(),
    })
    .nullable(),
  treatment: z.object({
    openCount: z.number().int(),
    scheduledCount: z.number().int(),
    /** Null when the caller lacks `money.view`. */
    totalFeeCents: z.number().int().nullable(),
  }),
});
export type PatientTiles = z.infer<typeof PatientTilesSchema>;

export const FieldEditabilitySchema = z.object({
  editable: z.boolean(),
  reason: z.string().nullable(),
});
export type FieldEditability = z.infer<typeof FieldEditabilitySchema>;

/**
 * Where an edit goes and which fields take one. PMS-mastered rows accept only
 * what the office's capability manifest allows for `patient.update`;
 * everything else says why (BUILD-PLAN 2.5.1 rule 5).
 */
export const PatientEditCapabilitySchema = z.object({
  canEdit: z.boolean(),
  reason: z.string().nullable(),
  writeTarget: z.enum(['velora', 'pms']),
  pmsSystem: PmsSystemSchema.nullable(),
  fields: z.record(z.enum(PATIENT_EDIT_FIELDS), FieldEditabilitySchema),
  contactPoints: FieldEditabilitySchema,
  /**
   * How a save reaches the PMS (FF-CC): `velora` (Velora keeps the record),
   * `command` (a `patient.update` is queued through the V09 gate) or `task`
   * (the gate is closed, e.g. not WRITE_READY: saving creates an "Enter in
   * <PMS>" task with the changes and leaves the PMS-kept row as it is).
   * Optional for older servers.
   */
  route: z.enum(['velora', 'command', 'task']).optional(),
});
export type PatientEditCapability = z.infer<typeof PatientEditCapabilitySchema>;

export const PatientConflictSchema = z.object({
  id: IdSchema,
  fields: z.array(z.string()),
  base: z.record(z.string(), z.unknown()),
  ours: z.record(z.string(), z.unknown()),
  theirs: z.record(z.string(), z.unknown()),
  createdAt: InstantSchema,
});
export type PatientConflict = z.infer<typeof PatientConflictSchema>;

/* ---------- data availability (V18; mirrors V06 `dataAvailability`) ---------- */

/**
 * What the patient's PMS connection actually delivers. A screen never turns a
 * feed Velora does not receive into a fact: no "No medical alerts on file",
 * no "Self pay", no "$0 paid up", no "No household" (audit PAT-01..10,
 * PT-01..06). The API computes it from the link's adapter (`patientFeeds`),
 * with the same table as V06's `dataAvailability`.
 */
export const PATIENT_FEEDS = [
  'medicalAlerts',
  'insurance',
  'households',
  'demographics',
  'ledger',
  'firstVisit',
  'apptCreatedAt',
  'recallTypeNames',
  'commlog',
  'documents',
] as const;
export const PatientFeedSchema = z.enum(PATIENT_FEEDS);
export type PatientFeed = z.infer<typeof PatientFeedSchema>;

/**
 * - `velora`: Velora is the record (no PMS, or the PMS handed this over);
 * - `synced`: the feed arrives from the PMS;
 * - `partial`: some of it arrives (the reason says what);
 * - `not_synced`: nothing arrives; the screen must say so.
 */
export const PATIENT_FEED_STATES = ['velora', 'synced', 'partial', 'not_synced'] as const;
export const PatientFeedStateSchema = z.enum(PATIENT_FEED_STATES);
export type PatientFeedState = z.infer<typeof PatientFeedStateSchema>;

export const PatientFeedAvailabilitySchema = z.object({
  state: PatientFeedStateSchema,
  /** One line for staff, no PHI. */
  reason: z.string().max(300),
});
export type PatientFeedAvailability = z.infer<typeof PatientFeedAvailabilitySchema>;

export const PatientDataAvailabilitySchema = z.object({
  /** The link's adapter, null for a Velora-only record. */
  adapter: PmsAdapterSchema.nullable(),
  pmsSystem: PmsSystemSchema.nullable(),
  /** "Dentrix", "Open Dental", "Eaglesoft"; null for a Velora-only record. */
  pmsLabel: z.string().nullable(),
  feeds: z.record(PatientFeedSchema, PatientFeedAvailabilitySchema),
  /**
   * The PMS says some insurance is on file (Dentrix `insurance_id`, V05
   * `patient.pms_has_insurance_hint`); null when unknown. Never read null as
   * "self pay".
   */
  hasInsuranceHint: z.boolean().nullable(),
});
export type PatientDataAvailability = z.infer<typeof PatientDataAvailabilitySchema>;

const PMS_NAME: Record<'opendental' | 'dentrix' | 'eaglesoft', string> = {
  opendental: 'Open Dental',
  dentrix: 'Dentrix',
  eaglesoft: 'Eaglesoft',
};

/** "Dentrix", "Open Dental", "Eaglesoft", or "the PMS" when unknown. */
export function pmsName(system: 'opendental' | 'dentrix' | 'eaglesoft' | null | undefined): string {
  return system ? PMS_NAME[system] : 'the PMS';
}

interface FeedInput {
  patient: { origin: 'pms' | 'velora' };
  availability?: PatientDataAvailability | undefined;
}

/**
 * The state of one feed for a patient overview. Safe default: a PMS record
 * from a server that sends no availability reads as `not_synced`, so a
 * missing field can never become "No medical alerts on file".
 */
export function patientFeedState(o: FeedInput, feed: PatientFeed): PatientFeedState {
  const f = o.availability?.feeds[feed];
  if (f) return f.state;
  return o.patient.origin === 'pms' ? 'not_synced' : 'velora';
}

/**
 * True when an empty value must read "not synced" rather than as a fact.
 * Partial demographics count: the fields v1 sends are filled, the others are
 * the ones that come back empty.
 */
export function feedNotSynced(o: FeedInput, feed: PatientFeed): boolean {
  const s = patientFeedState(o, feed);
  return s === 'not_synced' || (s === 'partial' && feed === 'demographics');
}

/** The PMS name for an overview ("Dentrix"), or "the PMS". */
export function overviewPmsName(o: {
  patient: { pmsSystem: 'opendental' | 'dentrix' | 'eaglesoft' | null };
  availability?: PatientDataAvailability | undefined;
}): string {
  return o.availability?.pmsLabel ?? pmsName(o.patient.pmsSystem);
}

/** Clinical-safety copy (PAT-01, PT-06): shown in red wherever alerts appear. */
export function medicalAlertsNotSyncedText(pms: string): string {
  return `Medical alerts, allergies and premedication are NOT synced from ${pms}. Check ${pms} before treatment.`;
}

/** Insurance copy (PAT-02, PT-03, DIC-F-04): never "Self pay" for a PMS record. */
export function insuranceNotSyncedText(pms: string): string {
  return `Insurance not synced from ${pms}`;
}

/** Family copy (PAT-03, PT-01): never "No household" for a PMS record. */
export function familyNotSyncedText(pms: string): string {
  return `Family not synced from ${pms}`;
}

/** Ledger copy (PAT-10, PT-04): never "$0 Paid up" for a PMS record. */
export function ledgerNotSyncedText(pms: string): string {
  return `Ledger not synced from ${pms}`;
}

/** A demographic field the PMS does not send (PAT-06, PAT-07, PT-02). */
export function fieldNotSyncedText(pms: string): string {
  return `Not synced from ${pms}`;
}

/** `GET /v1/patients/:id`: header, tiles, rail and family in one round trip. */
export const PatientOverviewSchema = z.object({
  patient: PatientSchema,
  age: z.number().int().nullable(),
  /** Practice-local today, so the client computes ages and due dates on the same clock. */
  today: LocalDateSchema,
  primaryProvider: PersonRefSchema.nullable(),
  secondaryProvider: PersonRefSchema.nullable(),
  referralSource: z.string().nullable(),
  /** The patient's employer name (from `patient.employer_id`); absent from older servers. */
  employerName: z.string().nullable().optional(),
  contactPoints: z.array(ContactPointViewSchema),
  alerts: z.array(PatientAlertViewSchema),
  household: HouseholdViewSchema.nullable(),
  tiles: PatientTilesSchema,
  upcoming: z.array(UpcomingVisitSchema),
  edit: PatientEditCapabilitySchema,
  conflicts: z.array(PatientConflictSchema),
  moneyVisible: z.boolean(),
  /**
   * What the PMS link delivers (V18). Optional only so older servers parse;
   * read it through `patientFeedState`, which treats a missing value on a PMS
   * record as not synced.
   */
  availability: PatientDataAvailabilitySchema.optional(),
});
export type PatientOverview = z.infer<typeof PatientOverviewSchema>;

/* ---------- timeline: one chronological stream (M04) ---------- */

export const TIMELINE_GROUPS = ['calls', 'texts', 'visits', 'notes', 'billing', 'forms'] as const;
export const TimelineGroupSchema = z.enum(TIMELINE_GROUPS);
export type TimelineGroup = z.infer<typeof TimelineGroupSchema>;

export const TIMELINE_KINDS = [
  'visit',
  'missed',
  'booked',
  'call',
  'voicemail',
  'text',
  'email',
  'note',
  'payment',
  'claim',
  'form',
  'review',
] as const;
export const TimelineKindSchema = z.enum(TIMELINE_KINDS);
export type TimelineKind = z.infer<typeof TimelineKindSchema>;

export const TIMELINE_KIND_GROUP: Record<TimelineKind, TimelineGroup> = {
  visit: 'visits',
  missed: 'visits',
  booked: 'visits',
  call: 'calls',
  voicemail: 'calls',
  text: 'texts',
  email: 'texts',
  note: 'notes',
  review: 'notes',
  payment: 'billing',
  claim: 'billing',
  form: 'forms',
};

export const TimelineToneSchema = z.enum(['success', 'warning', 'danger', 'info', 'neutral']);
export type TimelineTone = z.infer<typeof TimelineToneSchema>;

export const TimelineItemSchema = z.object({
  /** `<source>:<uuid>`, stable across refetches. */
  id: z.string().min(3).max(80),
  kind: TimelineKindSchema,
  group: TimelineGroupSchema,
  occurredAt: InstantSchema,
  locationId: IdSchema.nullable(),
  direction: z.enum(['in', 'out']).nullable(),
  actorName: z.string().nullable(),
  title: z.string(),
  body: z.string().nullable(),
  status: z.object({ tone: TimelineToneSchema, label: z.string() }).nullable(),
  amountCents: z.number().int().nullable(),
  durationSec: z.number().int().nullable(),
  pinned: z.boolean(),
  tag: z.string().nullable(),
  procedures: z.array(
    z.object({
      code: z.string(),
      description: z.string().nullable(),
      tooth: z.string().nullable(),
      surfaces: z.string().nullable(),
      feeCents: z.number().int().nullable(),
    }),
  ),
  /** Length, operatory and provider for visits. */
  visit: z
    .object({
      operatoryName: z.string().nullable(),
      lengthMin: z.number().int(),
      providerName: z.string().nullable(),
    })
    .nullable(),
});
export type TimelineItem = z.infer<typeof TimelineItemSchema>;

export const TimelineFilterSchema = z.enum(['all', ...TIMELINE_GROUPS]);
export type TimelineFilter = z.infer<typeof TimelineFilterSchema>;

export const PatientTimelineQuerySchema = z
  .object({
    group: TimelineFilterSchema.default('all'),
    /** Only activity at this office (the "Only <office>" toggle). */
    locationId: IdSchema.optional(),
    /** Page older than this instant. */
    before: InstantSchema.optional(),
    limit: z.coerce.number().int().min(1).max(300).default(150),
  })
  .strict();
export type PatientTimelineQuery = z.output<typeof PatientTimelineQuerySchema>;

export const PatientTimelineSchema = z.object({
  items: z.array(TimelineItemSchema),
  /** Counts over the whole stream (all offices in scope), for the filter chips. */
  counts: z.record(TimelineFilterSchema, z.number().int()),
  /** Offices that appear in the stream. */
  locationIds: z.array(IdSchema),
  nextBefore: InstantSchema.nullable(),
  /**
   * How far back the PMS history in Velora goes for this patient's office
   * (PT-10): "Dentrix history from May 2026". Null for Velora-only records.
   */
  pmsHistory: z
    .object({
      pmsLabel: z.string(),
      /** Earliest completed PMS visit Velora holds for the office, null when none. */
      from: LocalDateSchema.nullable(),
    })
    .nullable()
    .optional(),
});
export type PatientTimeline = z.infer<typeof PatientTimelineSchema>;

/* ---------- PMS documents (wave 6, metadata only) ---------- */

/** One document in the PMS document list (no file: it stays in the PMS). */
export const PmsDocumentItemSchema = z.object({
  id: IdSchema,
  locationId: IdSchema,
  title: z.string(),
  category: z.string().nullable(),
  fileName: z.string().nullable(),
  imageType: z.enum(['document', 'radiograph', 'photo', 'file', 'attachment']).nullable(),
  docDate: LocalDateSchema.nullable(),
  toothNumbers: z.string().nullable(),
  /** Page count from the PMS (Dentrix), null when unknown. */
  pages: z.number().int().nullable(),
  /**
   * The document's file in Velora (read-only copy pushed by the office
   * connector). `stored`: viewable at `/v1/patients/:id/pms-documents/:docId/content`
   * (`viewable` says whether the browser shows it inline: PDF, JPEG, PNG,
   * GIF, WebP, or a TIFF with a PNG preview). `unsupported`: the connector
   * could not send it (`reason`, e.g. `encrypted_container`). `not_uploaded`:
   * not pushed yet.
   */
  file: z.object({
    state: z.enum(['stored', 'unsupported', 'not_uploaded']),
    mimeType: z.string().nullable(),
    viewable: z.boolean(),
    sizeBytes: z.number().int().nullable(),
    reason: z.string().nullable(),
  }),
});
export type PmsDocumentItem = z.infer<typeof PmsDocumentItemSchema>;

export const PatientPmsDocumentsSchema = z.object({
  /** Whether the PMS document list is read for the patient's office. */
  availability: PatientFeedAvailabilitySchema,
  /** "Dentrix", "Open Dental"; null for a Velora-only record. */
  pmsLabel: z.string().nullable(),
  items: z.array(PmsDocumentItemSchema),
});
export type PatientPmsDocuments = z.infer<typeof PatientPmsDocumentsSchema>;

/* ---------- writes ---------- */

export const PatientUpdateResultSchema = z.object({
  patient: PatientSchema,
  applied: z.array(z.string()),
  unchanged: z.array(z.string()),
  conflicts: z.array(
    z.object({ field: z.string(), base: z.unknown(), ours: z.unknown(), theirs: z.unknown() }),
  ),
  /** Outbox command carrying the change to the PMS, when the row is PMS-mastered. */
  commandId: IdSchema.nullable(),
  /** The "Enter in <PMS>" task holding the changes, when the write-back gate is closed (FF-CC). */
  taskId: IdSchema.nullable().optional(),
  /**
   * After an address change on a household member: who shared the old address
   * (offer "Apply to N others") and who lives elsewhere (PF-08).
   */
  addressFanout: z
    .object({
      fromAddress: PostalAddressSchema,
      sameAddress: z.array(PersonRefSchema),
      otherAddress: z.array(PersonRefSchema),
    })
    .nullable(),
});
export type PatientUpdateResult = z.infer<typeof PatientUpdateResultSchema>;

/** `POST /v1/patients/:id/address-fanout`: copy this patient's address to members. */
export const AddressFanoutRequestSchema = z
  .object({
    /** Members are updated only while they still hold this address. */
    fromAddress: PostalAddressSchema,
    patientIds: z.array(IdSchema).min(1).max(50),
    idempotencyKey: IdempotencyKeySchema,
  })
  .strict();
export type AddressFanoutRequest = z.infer<typeof AddressFanoutRequestSchema>;

export const AddressFanoutResultSchema = z.object({
  updated: z.array(PersonRefSchema),
  unchanged: z.array(PersonRefSchema.extend({ reason: z.string() })),
});
export type AddressFanoutResult = z.infer<typeof AddressFanoutResultSchema>;

export const ContactPointPatchSchema = z
  .object({
    expectedVersion: RowVersionSchema,
    idempotencyKey: IdempotencyKeySchema,
    value: z.string().trim().min(3).max(320).optional(),
    phoneType: PhoneTypeSchema.nullable().optional(),
    isPrimary: z.literal(true).optional(),
    /** Soft-deletes the contact point. */
    remove: z.literal(true).optional(),
  })
  .strict()
  .refine(
    (p) => Object.keys(p).some((k) => k !== 'expectedVersion' && k !== 'idempotencyKey'),
    'no changes',
  );
export type ContactPointPatch = z.infer<typeof ContactPointPatchSchema>;

/**
 * `PATCH /v1/patients/contact-points/:id`. `notice` says what the user must
 * still do in the PMS: a phone column the gateway cannot clear yet stays
 * filled there (WPC-4). Null when nothing is left to do.
 */
export const ContactPointPatchResultSchema = z.object({
  contactPoint: ContactPointViewSchema.nullable(),
  notice: z.string().max(300).nullable(),
});
export type ContactPointPatchResult = z.infer<typeof ContactPointPatchResultSchema>;

export const CONSENT_CATEGORIES = [
  'transactional',
  'billing',
  'marketing',
  'review',
  'email',
] as const;
export const ConsentCategorySchema = z.enum(CONSENT_CATEGORIES);
export type ConsentCategory = z.infer<typeof ConsentCategorySchema>;

/** `POST /v1/patients/contact-points/:id/consent`: a staff change, logged with its time. */
export const ConsentChangeRequestSchema = z
  .object({
    category: ConsentCategorySchema,
    granted: z.boolean(),
    idempotencyKey: IdempotencyKeySchema,
  })
  .strict();
export type ConsentChangeRequest = z.infer<typeof ConsentChangeRequestSchema>;

export const AlertSeveritySchema = z.enum(['info', 'warning', 'critical']);

export const PatientAlertInputSchema = z
  .object({
    kind: PatientAlertSchema.shape.kind,
    description: z.string().trim().min(1).max(500),
    severity: AlertSeveritySchema,
    snomedCode: z
      .string()
      .regex(/^\d{6,18}$/)
      .nullable()
      .default(null),
    scope: AlertScopeSchema.default('patient'),
    expiresAt: LocalDateSchema.nullable().default(null),
    idempotencyKey: IdempotencyKeySchema,
  })
  .strict();
export type PatientAlertInput = z.input<typeof PatientAlertInputSchema>;

export const PatientAlertPatchSchema = z
  .object({
    expectedVersion: RowVersionSchema,
    description: z.string().trim().min(1).max(500).optional(),
    severity: AlertSeveritySchema.optional(),
    expiresAt: LocalDateSchema.nullable().optional(),
    scope: AlertScopeSchema.optional(),
    /** False retires the alert; it stays in history. */
    active: z.boolean().optional(),
  })
  .strict();
export type PatientAlertPatch = z.infer<typeof PatientAlertPatchSchema>;

export const AlertAckSchema = z.object({ acknowledgedAt: InstantSchema });

export const PatientNoteInputSchema = z
  .object({
    body: z.string().trim().min(1).max(4000),
    pinned: z.boolean().default(false),
    idempotencyKey: IdempotencyKeySchema,
  })
  .strict();
export type PatientNoteInput = z.input<typeof PatientNoteInputSchema>;

export const PatientNotePatchSchema = z.object({ pinned: z.boolean() }).strict();

export const CALL_OUTCOMES = ['answered', 'voicemail', 'no_answer', 'wrong_number'] as const;
export const CallOutcomeSchema = z.enum(CALL_OUTCOMES);

/** A call made outside Velora's phone system, logged by hand. */
export const PatientCallLogInputSchema = z
  .object({
    direction: z.enum(['inbound', 'outbound']),
    outcome: CallOutcomeSchema,
    body: z.string().trim().max(4000).default(''),
    idempotencyKey: IdempotencyKeySchema,
  })
  .strict();
export type PatientCallLogInput = z.input<typeof PatientCallLogInputSchema>;

/* ---------- household operations (patient-file 4.3) ---------- */

export const SetGuarantorRequestSchema = z
  .object({ patientId: IdSchema, idempotencyKey: IdempotencyKeySchema })
  .strict();
export type SetGuarantorRequest = z.infer<typeof SetGuarantorRequestSchema>;

export const RelationshipPatchSchema = z
  .object({ relationship: HouseholdRelationshipSchema })
  .strict();

export const AccountNotePatchSchema = z
  .object({ expectedVersion: RowVersionSchema, accountNote: z.string().max(4000).nullable() })
  .strict();

export const HouseholdMoveTargetSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('existing'), householdId: IdSchema }),
  z.object({ kind: z.literal('new') }),
]);
export type HouseholdMoveTarget = z.infer<typeof HouseholdMoveTargetSchema>;

export const HouseholdMovePreviewRequestSchema = z
  .object({ target: HouseholdMoveTargetSchema })
  .strict();

export const GuarantorCandidateSchema = PersonRefSchema.extend({
  age: z.number().int().nullable(),
  eligible: z.boolean(),
  reason: z.string().nullable(),
});
export type GuarantorCandidate = z.infer<typeof GuarantorCandidateSchema>;

export const HouseholdMovePreviewSchema = z.object({
  patient: PersonRefSchema,
  from: z.object({
    householdId: IdSchema.nullable(),
    name: z.string(),
    memberCount: z.number().int(),
  }),
  to: z.object({
    householdId: IdSchema.nullable(),
    name: z.string(),
    memberCount: z.number().int(),
  }),
  /** The member's own balance moves with them (null without `money.view`). */
  balanceCents: z.number().int().nullable(),
  /** Open claims stay attached to the same subscriber and plan. */
  openClaims: z.number().int(),
  /** The source household's account note is copied with a provenance line. */
  accountNoteCopied: z.boolean(),
  /** Moving a guarantor out of a household with others needs a replacement (PF-10). */
  needsNewGuarantor: z
    .object({ othersCount: z.number().int(), candidates: z.array(GuarantorCandidateSchema) })
    .nullable(),
  /** True only when the target is a new household. */
  becomesGuarantor: z.boolean(),
  /** Plain-language lines for the preview list. */
  effects: z.array(z.string()),
});
export type HouseholdMovePreview = z.infer<typeof HouseholdMovePreviewSchema>;

export const HouseholdMoveRequestSchema = z
  .object({
    target: HouseholdMoveTargetSchema,
    relationship: HouseholdRelationshipSchema,
    newGuarantorPatientId: IdSchema.optional(),
    idempotencyKey: IdempotencyKeySchema,
  })
  .strict();
export type HouseholdMoveRequest = z.infer<typeof HouseholdMoveRequestSchema>;

export const HouseholdMoveResultSchema = z.object({
  moveId: IdSchema,
  householdId: IdSchema,
  /** Undo is accepted until this instant. */
  undoUntil: InstantSchema,
});
export type HouseholdMoveResult = z.infer<typeof HouseholdMoveResultSchema>;

export const HouseholdSearchRequestSchema = z
  .object({ q: z.string().trim().min(2).max(100) })
  .strict();

export const HouseholdSearchResultSchema = z.object({
  households: z.array(
    z.object({
      id: IdSchema,
      name: z.string(),
      guarantorName: z.string().nullable(),
      memberCount: z.number().int(),
      lockedReason: z.string().nullable(),
    }),
  ),
});
export type HouseholdSearchResult = z.infer<typeof HouseholdSearchResultSchema>;

/* ---------- referred by (role test FD-8) ---------- */

export const REFERRAL_KINDS = [
  'patient',
  'provider',
  'marketing',
  'web',
  'insurance',
  'other',
] as const;
export const ReferralKindSchema = z.enum(REFERRAL_KINDS);
export type ReferralKind = z.infer<typeof ReferralKindSchema>;
export const REFERRAL_KIND_LABEL: Record<ReferralKind, string> = {
  patient: 'Patient or family',
  provider: 'Doctor or specialist',
  marketing: 'Marketing or ad',
  web: 'Website or online',
  insurance: 'Insurance plan',
  other: 'Other',
};

/** `GET /v1/patients/referral-sources`: the practice's referral sources, most used first. */
export const ReferralSourcesSchema = z.object({
  sources: z.array(
    z.object({
      id: IdSchema,
      name: z.string(),
      kind: ReferralKindSchema,
      patients: z.number().int(),
    }),
  ),
});
export type ReferralSources = z.infer<typeof ReferralSourcesSchema>;

/** A referral pick: an existing source, a new one by name, or none. */
export const ReferralPickSchema = z.union([
  z.object({ referralSourceId: IdSchema }).strict(),
  z.object({ name: z.string().trim().min(2).max(120), kind: ReferralKindSchema }).strict(),
]);
export type ReferralPickInput = z.infer<typeof ReferralPickSchema>;

/** `PUT /v1/patients/:id/referral`. Velora's own field: nothing is written to the PMS. */
export const SetReferralRequestSchema = z
  .object({ referral: ReferralPickSchema.nullable() })
  .strict();
export type SetReferralRequest = z.infer<typeof SetReferralRequestSchema>;
export const SetReferralResultSchema = z.object({
  referralSourceId: IdSchema.nullable(),
  referralSource: z.string().nullable(),
});

/**
 * `GET /v1/patients/:id/referral` (W15): the patient's referral source and
 * whether they are new (no completed visit yet), so booking can ask "How did
 * they hear about us?" while the caller is still on the phone.
 */
export const PatientReferralSchema = z.object({
  referralSourceId: IdSchema.nullable(),
  referralSource: z.string().nullable(),
  newPatient: z.boolean(),
  canEdit: z.boolean(),
});
export type PatientReferral = z.infer<typeof PatientReferralSchema>;

/* ---------- new patients and households (patient-file 4.2) ---------- */

/** `GET /v1/patients/offices`: where new patients can be created in Velora. */
export const CreateOfficesSchema = z.object({
  offices: z.array(
    z.object({
      locationId: IdSchema,
      canCreate: z.boolean(),
      reason: z.string().nullable(),
      /**
       * NEW-WF-07 (PF-01/02): at a PMS office the household is saved in Velora as
       * "pending in <PMS>" with a task, and links to the PMS record when it syncs.
       */
      staged: z.boolean().default(false),
      pmsLabel: z.string().nullable().default(null),
    }),
  ),
});
export type CreateOffices = z.infer<typeof CreateOfficesSchema>;

/** `POST /v1/patients/duplicates`: live duplicate check while typing (PF-05). */
export const DuplicateCheckRequestSchema = z
  .object({
    firstName: z.string().trim().max(100).default(''),
    lastName: z.string().trim().min(1).max(100),
    birthdate: LocalDateSchema.nullable().default(null),
    phone: z.string().trim().max(40).nullable().default(null),
    email: z.string().trim().max(320).nullable().default(null),
  })
  .strict();
export type DuplicateCheckInput = z.input<typeof DuplicateCheckRequestSchema>;

export const DuplicateCandidateSchema = z.object({
  id: IdSchema,
  firstName: z.string(),
  lastName: z.string(),
  preferredName: z.string().nullable(),
  birthdate: LocalDateSchema.nullable(),
  homeLocationId: IdSchema.nullable(),
  householdId: IdSchema.nullable(),
  status: PatientStatusSchema,
  /** Why it looks like the same person, in words. */
  reason: z.string(),
});
export type DuplicateCandidate = z.infer<typeof DuplicateCandidateSchema>;

export const DuplicateCheckResultSchema = z.object({
  candidates: z.array(DuplicateCandidateSchema),
});

export const NewMemberSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('new'),
      firstName: NameSchema,
      lastName: NameSchema,
      preferredName: z.string().trim().max(100).nullable().default(null),
      birthdate: LocalDateSchema,
      /** Required, never defaulted (PF-04). */
      gender: PatientGenderSchema,
      mobile: z.string().trim().max(40).nullable().default(null),
      email: z.string().trim().max(320).nullable().default(null),
      relationship: HouseholdRelationshipSchema.default('other'),
    })
    .strict(),
  z
    .object({
      kind: z.literal('existing'),
      patientId: IdSchema,
      relationship: HouseholdRelationshipSchema.default('other'),
    })
    .strict(),
]);
export type NewMember = z.input<typeof NewMemberSchema>;

/** `POST /v1/patients/households`: the household composer saves every row at once (PF-02). */
export const HouseholdCreateRequestSchema = z
  .object({
    homeLocationId: IdSchema,
    address: PostalAddressSchema,
    members: z.array(NewMemberSchema).min(1).max(20),
    /** Index into `members` of the guarantor; must be an adult (PF-03). */
    guarantorIndex: z.number().int().min(0),
    /** How the family heard of the office; set on every new member (FD-8). */
    referral: ReferralPickSchema.nullable().optional(),
    idempotencyKey: IdempotencyKeySchema,
  })
  .strict()
  .refine((r) => r.guarantorIndex < r.members.length, {
    path: ['guarantorIndex'],
    message: 'the guarantor must be one of the members',
  });
export type HouseholdCreateRequest = z.input<typeof HouseholdCreateRequestSchema>;

export const HouseholdCreateResultSchema = z.object({
  householdId: IdSchema,
  patientIds: z.array(IdSchema),
  guarantorPatientId: IdSchema,
  /** Set when the office's PMS keeps patients: saved here as pending, with a task (NEW-WF-07). */
  staged: z.object({ pmsLabel: z.string(), taskId: IdSchema.nullable() }).nullable().default(null),
});
export type HouseholdCreateResult = z.infer<typeof HouseholdCreateResultSchema>;

/**
 * `POST /v1/patients/households/:id/new-member`: Family File "Add family
 * member" creates a new person (a child) in this family (role test FD-7), not
 * only moves someone who is already a patient. At a PMS office the person is
 * saved as pending in the PMS on the family's one "Add in <PMS>" task.
 */
export const HouseholdNewMemberRequestSchema = z
  .object({
    firstName: NameSchema,
    lastName: NameSchema,
    preferredName: z.string().trim().max(100).nullable().default(null),
    birthdate: LocalDateSchema,
    gender: PatientGenderSchema,
    mobile: z.string().trim().max(40).nullable().default(null),
    email: z.string().trim().max(320).nullable().default(null),
    relationship: HouseholdRelationshipSchema.default('child'),
    /** Keep the guarantor's address (default) or leave it empty. */
    sameAddress: z.boolean().default(true),
    idempotencyKey: IdempotencyKeySchema,
  })
  .strict();
export type HouseholdNewMemberRequest = z.input<typeof HouseholdNewMemberRequestSchema>;
export const HouseholdNewMemberResultSchema = z.object({
  /** Null when the family is kept in the PMS: only a task was filed. */
  patientId: IdSchema.nullable(),
  householdId: IdSchema,
  staged: z.object({ pmsLabel: z.string(), taskId: IdSchema.nullable() }).nullable(),
});
export type HouseholdNewMemberResult = z.infer<typeof HouseholdNewMemberResultSchema>;

/**
 * `POST /v1/patients/quick`: the booking panel's inline "New patient"
 * (WF-07, X-08). One adult patient as their own household, only where Velora
 * owns demographics. The shared duplicate check runs first: when it finds
 * someone and `createAnyway` is not set, nothing is created and the
 * candidates come back to pick from.
 */
export const QuickPatientCreateRequestSchema = z
  .object({
    homeLocationId: IdSchema,
    firstName: NameSchema,
    lastName: NameSchema,
    birthdate: LocalDateSchema,
    gender: PatientGenderSchema,
    mobile: z.string().trim().max(40).nullable().default(null),
    /** The lead's email, carried into the record (FD-11). */
    email: z.string().trim().max(320).nullable().default(null),
    /** How they heard about the office (W15 marketing ROI); Velora's own field. */
    referral: ReferralPickSchema.nullable().optional(),
    createAnyway: z.boolean().default(false),
    idempotencyKey: IdempotencyKeySchema,
  })
  .strict();
export type QuickPatientCreateRequest = z.input<typeof QuickPatientCreateRequestSchema>;

export const QuickPatientCreateResultSchema = z.object({
  /** Null when duplicates were found and nothing was created. */
  patientId: IdSchema.nullable(),
  candidates: z.array(DuplicateCandidateSchema),
});
export type QuickPatientCreateResult = z.infer<typeof QuickPatientCreateResultSchema>;

/** `GET /v1/patients/providers`: choices for the primary and secondary provider fields. */
export const ProviderChoicesSchema = z.object({
  providers: z.array(
    z.object({
      id: IdSchema,
      name: z.string(),
      kind: z.enum(['dentist', 'hygienist', 'assistant', 'other']),
      homeLocationId: IdSchema.nullable(),
    }),
  ),
});
export type ProviderChoices = z.infer<typeof ProviderChoicesSchema>;

export const ConflictResolutionRequestSchema = z
  .object({ resolution: z.enum(['ours', 'theirs']), idempotencyKey: IdempotencyKeySchema })
  .strict();
export type ConflictResolutionRequest = z.infer<typeof ConflictResolutionRequestSchema>;
